import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { FormMode } from '../../../shared/types/forms.types';
import { RoleDTO } from '../../../shared/types/role.types';

export interface RoleFormPayload {
  name: string;
  description?: string;
  composite?: boolean;
}

@Component({
  selector: 'app-role-form',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './role-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleFormComponent implements OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);

  @Input() role: RoleDTO | null = null;
  @Input() mode: FormMode = 'create';
  @Input() submitting = false;
  @Input() serverError: string | null = null;

  @Output() readonly save = new EventEmitter<RoleFormPayload>();
  @Output() readonly cancel = new EventEmitter<void>();

  readonly form = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(/^[a-zA-Z0-9_\-.]+$/),
      ],
    ],
    description: ['', [Validators.maxLength(255)]],
    composite: [false],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['role'] && this.role) {
      this.form.patchValue(
        {
          name: this.role.name ?? '',
          description: this.role.description ?? '',
          composite: this.role.composite ?? false,
        },
        { emitEvent: false },
      );
    }
    if (changes['mode']) {
      // The role name is the URL slug — disable renaming in edit mode to
      // avoid the gotcha of changing the lookup key.
      if (this.mode === 'edit') {
        this.form.controls.name.disable({ emitEvent: false });
      } else {
        this.form.controls.name.enable({ emitEvent: false });
      }
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.save.emit({
      name: raw.name.trim(),
      description: raw.description?.trim() || undefined,
      composite: raw.composite,
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }

  errorFor(name: keyof typeof this.form.controls): string | null {
    const c: AbstractControl = this.form.controls[name];
    if (!c.errors || (!c.touched && !c.dirty)) return null;
    if (c.errors['required']) return 'Champ obligatoire.';
    if (c.errors['minlength'])
      return `${c.errors['minlength'].requiredLength} caractères minimum.`;
    if (c.errors['maxlength'])
      return `${c.errors['maxlength'].requiredLength} caractères maximum.`;
    if (c.errors['pattern'])
      return 'Lettres, chiffres, . _ - uniquement.';
    return 'Valeur invalide.';
  }
}
